import React from 'react';
import {
  Action,
  ConfirmModal, Form, FormHandlers, Portal, Spacing, Text, useModal, useNotifications,
} from '@doveit/bricks';
import * as Yup from 'yup';
import { Invoice } from '../../../providers/api/dtos';
import { InvoiceStatus } from '../../../domain/types';
import { updateInvoice } from '../../../providers/api/invoice/invoiceProvider';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { useComputedInvoiceStatus } from '../../hooks/use-computed-invoice-status/useComputedInvoiceStatus';

export const UPDATE_INVOICE_TO_UNPAID_ERROR_MESSAGE = 'Non è stato possibile aggiornare lo stato del pagamento';
export const UPDATE_INVOICE_TO_UNPAID_SUCCESS_MESSAGE = 'Stato del pagamento aggiornato con successo';

const validationSchema = Yup.object().shape({
  notes: Yup.string().required("Il campo 'Note' è obbligatorio"),
});

interface ChildFnProps {
  openMarkUnpaidInvoiceModal: VoidFunction
}

export interface MarkUnpaidInvoiceActionProps {
  invoice: Invoice,
  onSuccess?: (invoiceToUpdate: Invoice) => void,
  children?: (props: ChildFnProps) => React.ReactNode
}

const MarkUnpaidInvoiceAction: React.FC<MarkUnpaidInvoiceActionProps> = ({
  invoice,
  onSuccess,
  children,
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const unpaidInvoiceFormRef = React.useRef() as React.MutableRefObject<FormHandlers>;
  const { user, mainUserRole } = useRBAC();
  const { canBeUpdatedTo } = useComputedInvoiceStatus(invoice);

  const unpaidInvoiceModal = useModal();
  const { addSuccess, addError } = useNotifications();

  const submitUnpaidInvoiceForm = React.useCallback(() => {
    unpaidInvoiceFormRef.current.handleSubmit();
  }, []);

  const openUnpaidInvoiceModal = React.useCallback(() => unpaidInvoiceModal.open(), [unpaidInvoiceModal]);
  const closeUnpaidInvoiceModal = React.useCallback(() => unpaidInvoiceModal.close(), [unpaidInvoiceModal]);

  const setInvoiceToUnpaid = React.useCallback(async (values: { notes: string }) => {
    try {
      setIsSaving(true);

      const invoiceToUpdate: Invoice = {
        ...invoice,
        status: InvoiceStatus.NOT_PAID,
        notes: [
          {
            author: user.name,
            text: values.notes.trim(),
            role: mainUserRole,
            date: new Date().toISOString(),
          },
          ...invoice.notes || [],
        ],
      };

      const updatedInvoice = await updateInvoice(invoice.id!, invoiceToUpdate);

      setIsSaving(false);

      addSuccess(UPDATE_INVOICE_TO_UNPAID_SUCCESS_MESSAGE);
      onSuccess?.(updatedInvoice);
      unpaidInvoiceModal.close();
    } catch (_) {
      setIsSaving(false);
      addError(UPDATE_INVOICE_TO_UNPAID_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, invoice, mainUserRole, onSuccess, unpaidInvoiceModal, user.name]);

  if (!canBeUpdatedTo(InvoiceStatus.NOT_PAID)) {
    return null;
  }

  return (
    <>
      {children
        ? (children({ openMarkUnpaidInvoiceModal: unpaidInvoiceModal.open }))
        : (
          <Action
            label="Segna come non pagato"
            aria-label="Azione per segnare il pagamento come non pagato"
            onClick={openUnpaidInvoiceModal}
            size="S"
            loading={isSaving}
          />
        )}
      <Portal>
        <ConfirmModal
          {...unpaidInvoiceModal}
          isOpen={unpaidInvoiceModal.isOpen}
          aria-label="Modale per segnare il pagamento come non pagato"
          saving={isSaving}
          title="Segna come non pagato"
          onConfirm={submitUnpaidInvoiceForm}
          onAbort={closeUnpaidInvoiceModal}
        >
          <Text.Body>
            Confermando l&apos;operazione il pagamento sarà segnato come non pagato.
          </Text.Body>
          <Spacing margin={[200, 0]}>
            <Form
              innerRef={unpaidInvoiceFormRef}
              validationSchema={validationSchema}
              initialValues={{ notes: '' }}
              onSubmit={setInvoiceToUnpaid}
            >
              <Form.Item>
                <Form.TextArea
                  label="Note"
                  name="notes"
                  aria-label="Campo per inserire la motivazione del mancato pagamento"
                  placeholder="Inserisci la motivazione del mancato pagamento"
                  required
                />
              </Form.Item>
            </Form>
          </Spacing>
        </ConfirmModal>
      </Portal>
    </>

  );
};

export default MarkUnpaidInvoiceAction;
